import React, { useEffect, useState, useRef } from 'react';
import {
  Box,
  Paper,
  Typography,
  useTheme,
  alpha,
  IconButton,
  Chip,
  LinearProgress,
} from '@mui/material';
import {
  AutoAwesome as AIIcon,
  TipsAndUpdates as InsightIcon,
  Close as CloseIcon,
  ArrowForward as ArrowIcon,
} from '@mui/icons-material';
import { motion, useAnimation, useInView, AnimatePresence } from 'framer-motion';

interface Insight {
  id: string;
  type: 'improvement' | 'concern' | 'recommendation' | 'achievement';
  title: string;
  message: string;
  metric?: string;
  actionLabel?: string;
  onAction?: () => void;
}

interface MetricInsightCardProps {
  insights: Insight[];
  onDismiss?: (insightId: string) => void;
  delay?: number;
}

// Removed typewriter effect for immediate, clean display

export const MetricInsightCard: React.FC<MetricInsightCardProps> = ({
  insights,
  onDismiss,
  delay = 0,
}) => {
  const theme = useTheme();
  const controls = useAnimation();
  const ref = useRef(null);
  const inView = useInView(ref, { once: true });
  const [currentInsightIndex, setCurrentInsightIndex] = useState(0);
  const [dismissedInsights, setDismissedInsights] = useState<string[]>([]);

  const visibleInsights = insights.filter(insight => !dismissedInsights.includes(insight.id));
  const currentInsight = visibleInsights[currentInsightIndex];

  useEffect(() => {
    if (inView) {
      controls.start('visible');
    }
  }, [controls, inView]);

  useEffect(() => {
    if (visibleInsights.length > 1) {
      const interval = setInterval(() => {
        setCurrentInsightIndex((prev) => (prev + 1) % visibleInsights.length);
      }, 8000); // Change insight every 8 seconds

      return () => clearInterval(interval);
    }
  }, [visibleInsights.length]);

  const handleDismiss = (insightId: string) => {
    setDismissedInsights(prev => [...prev, insightId]);
    if (onDismiss) {
      onDismiss(insightId);
    }
  };

  const getInsightColor = (type: Insight['type']) => {
    switch (type) {
      case 'improvement':
        return theme.palette.success.main;
      case 'concern':
        return theme.palette.error.main;
      case 'recommendation':
        return theme.palette.info.main;
      case 'achievement':
        return theme.palette.warning.main;
      default:
        return theme.palette.primary.main;
    }
  };

  const getInsightIcon = (type: Insight['type']) => {
    switch (type) {
      case 'improvement':
      case 'achievement':
        return '🎯';
      case 'concern':
        return '⚠️';
      case 'recommendation':
        return '💡';
      default:
        return '📊';
    }
  };

  const containerVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.3,
        delay: Math.min(delay, 0.1),
      },
    },
  };

  const insightVariants = {
    enter: { opacity: 0, x: 50 },
    center: { opacity: 1, x: 0 },
    exit: { opacity: 0, x: -50 },
  };

  if (visibleInsights.length === 0) return null;

  return (
    <motion.div
      ref={ref}
      initial="hidden"
      animate={controls}
      variants={containerVariants}
    >
      <Paper
        elevation={1}
        sx={{
          p: 3,
          borderRadius: 2,
          border: `1px solid ${alpha(theme.palette.divider, 0.08)}`,
          position: 'relative',
          overflow: 'hidden',
          backgroundColor: theme.palette.background.paper,
        }}
      >

        {/* Header */}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <AIIcon sx={{ color: theme.palette.primary.main }} />
            <Typography variant="subtitle2" fontWeight={600} color="text.secondary">
              AI Insights
            </Typography>
          </Box>
          
          {visibleInsights.length > 1 && (
            <Box sx={{ display: 'flex', gap: 0.5 }}>
              {visibleInsights.map((_, index) => (
                <Box
                  key={index}
                  sx={{
                    width: 6,
                    height: 6,
                    borderRadius: '50%',
                    backgroundColor: index === currentInsightIndex 
                      ? theme.palette.primary.main 
                      : alpha(theme.palette.divider, 0.3),
                    transition: 'all 0.3s ease',
                  }}
                />
              ))}
            </Box>
          )}
        </Box>

        {/* Insight content */}
        <AnimatePresence mode="wait">
          {currentInsight && (
            <motion.div
              key={currentInsight.id}
              variants={insightVariants}
              initial="enter"
              animate="center"
              exit="exit"
              transition={{ duration: 0.3 }}
            >
              <Box sx={{ mb: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <Typography variant="h4" sx={{ fontSize: '1.5rem' }}>
                    {getInsightIcon(currentInsight.type)}
                  </Typography>
                  <Chip
                    label={currentInsight.type}
                    size="small"
                    sx={{
                      backgroundColor: alpha(getInsightColor(currentInsight.type), 0.1),
                      color: getInsightColor(currentInsight.type),
                      fontWeight: 600,
                      textTransform: 'capitalize',
                    }}
                  />
                  {currentInsight.metric && (
                    <Chip
                      label={currentInsight.metric}
                      size="small"
                      variant="outlined"
                      sx={{ fontSize: '0.7rem' }}
                    />
                  )}
                </Box>

                <Typography variant="h6" fontWeight={600} gutterBottom>
                  {currentInsight.title}
                </Typography>
                
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ lineHeight: 1.6 }}
                >
                  {currentInsight.message}
                </Typography>
              </Box>

              {/* Action button */}
              {currentInsight.actionLabel && (
                <Box sx={{ mt: 2 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <IconButton
                      size="small"
                      onClick={() => handleDismiss(currentInsight.id)}
                      sx={{ color: 'text.secondary' }}
                    >
                      <CloseIcon fontSize="small" />
                    </IconButton>
                    
                    <motion.button
                      whileHover={{ scale: 1.05 }}
                      whileTap={{ scale: 0.95 }}
                      onClick={currentInsight.onAction}
                      style={{
                        background: `linear-gradient(135deg, ${getInsightColor(currentInsight.type)} 0%, ${alpha(getInsightColor(currentInsight.type), 0.8)} 100%)`,
                        color: 'white',
                        border: 'none',
                        borderRadius: theme.shape.borderRadius * 2,
                        padding: '8px 16px',
                        fontSize: '0.875rem',
                        fontWeight: 600,
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                      }}
                    >
                      {currentInsight.actionLabel}
                      <ArrowIcon fontSize="small" />
                    </motion.button>
                  </Box>
                </Box>
              )}
            </motion.div>
          )}
        </AnimatePresence>

        {/* Progress indicator for multiple insights */}
        {visibleInsights.length > 1 && (
          <Box sx={{ position: 'absolute', bottom: 0, left: 0, right: 0 }}>
            <LinearProgress
              variant="determinate"
              value={(currentInsightIndex + 1) / visibleInsights.length * 100}
              sx={{
                height: 2,
                backgroundColor: alpha(theme.palette.divider, 0.1),
                '& .MuiLinearProgress-bar': {
                  backgroundColor: getInsightColor(currentInsight?.type || 'recommendation'),
                },
              }}
            />
          </Box>
        )}
      </Paper>
    </motion.div>
  );
};

export default MetricInsightCard;